import { presentLexError } from '@/lib/user-notice';
import { useState, useEffect } from 'react';
import { getFeed, formatFeedDate, formatFeedDateLong, type NewsItem } from '@/lib/feed';
import { apiJson } from '@/lib/api';

const FEED_PAGE_URL = 'https://dumas.solutions/feed';
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://dumas.solutions';

const shortUrlCache = new Map<string, string>();

async function getShortUrl(item: NewsItem): Promise<string> {
  const cached = shortUrlCache.get(item.id);
  if (cached) return cached;
  const longUrl = item.url || FEED_PAGE_URL;
  try {
    const data = await apiJson<{ code: string }>('/api/shorten', {
      method: 'POST',
      body: JSON.stringify({ url: longUrl }),
    });
    if (data?.code) {
      const short = `${BASE_URL.replace(/\/$/, '')}/s/${data.code}`;
      shortUrlCache.set(item.id, short);
      return short;
    }
  } catch {
    // fallback to long URL
  }
  return longUrl;
}

function buildShareText(item: NewsItem, sourceUrl: string): string {
  const dateStr = formatFeedDate(item.date);
  return [
    `*${item.title}*`,
    dateStr,
    '',
    item.summary,
    '',
    '_Información proporcionada por AssistiA desde DU +_ ' + FEED_PAGE_URL,
    '',
    `Fuente: ${sourceUrl}`,
  ].join('\n');
}

function useShare(item: NewsItem) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const sourceUrl = await getShortUrl(item);
    const shareText = buildShareText(item, sourceUrl);

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: item.title, text: shareText });
        return;
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.open(
        `mailto:?subject=${encodeURIComponent(item.title)}&body=${encodeURIComponent(shareText)}`,
        '_blank'
      );
    }
  };

  return { handleShare, copied };
}

export function FeedPage() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getFeed()
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : 'Error al cargar el feed.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="lex-page">
      <div style={{ padding: '4px 0 16px' }}>
        <p style={{ margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
          Noticias
        </p>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--color-text)' }}>
          Feed legal
        </h1>
      </div>

      {error ? (
        <p style={{ color: 'var(--lex-danger)', fontSize: 14, marginBottom: 12 }}>{presentLexError(error)}</p>
      ) : null}

      {loading ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>Cargando…</p>
      ) : items.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>No hay noticias en el feed.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {items.map((item) => (
            <FeedCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </main>
  );
}

function FeedCard({ item }: { item: NewsItem }) {
  const { handleShare, copied } = useShare(item);

  return (
    <article style={{ borderBottom: '1px solid var(--color-border-soft)', padding: '18px 0' }}>
      {item.imageUrl ? (
        <div style={{ position: 'relative', paddingTop: '40%', background: 'var(--color-border)', borderRadius: 10, marginBottom: 14, overflow: 'hidden' }}>
          <img
            src={item.imageUrl}
            alt=""
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      ) : null}

      {/* meta row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'var(--color-accent)',
        }}>
          {item.source}
        </span>
        {item.categories?.slice(0, 1).map((cat) => (
          <span key={cat} style={{
            fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
            color: 'var(--color-text-muted)',
          }}>
            · {cat}
          </span>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-text-muted)' }}>
          {formatFeedDateLong(item.date)}
        </span>
      </div>

      {/* title */}
      <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.35, letterSpacing: '-0.01em' }}>
        {item.title}
      </h2>

      {/* summary */}
      {item.summary ? (
        <div style={{
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          background: 'var(--color-surface)',
          padding: '12px 14px',
          marginBottom: 12,
        }}>
          <p style={{ margin: '0 0 6px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
            Resumen DumiA
          </p>
          <div style={{ fontSize: 13, color: 'var(--color-text)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
            {item.summary}
          </div>
        </div>
      ) : null}

      {/* actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-accent)', textDecoration: 'none', letterSpacing: '0.02em' }}
          >
            Ver fuente ↗
          </a>
        ) : null}
        <button
          type="button"
          onClick={handleShare}
          style={{ padding: 0, border: 'none', background: 'none', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', cursor: 'pointer', letterSpacing: '0.02em' }}
          title={copied ? 'Enlace copiado' : 'Compartir'}
        >
          {copied ? 'Copiado ✓' : 'Compartir'}
        </button>
      </div>
    </article>
  );
}
